import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  ViewChildren,
  QueryList,
  ViewChild,
  AfterViewInit
} from '@angular/core';
import { Router } from '@angular/router';
import { MainMenuItem, MenuItem } from '../../interfaces/menu.interface';
import { RoutingService } from '../services/routing.service';
import { UserService } from '../../services/user.service';
import { MenuService } from '../../services/menu.service';
import { FocusTrapDirective } from '../directives/focus-trap.directive';

@Component({
  selector: 'ppl-menu-drop-button',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './menu-drop-button.component.html',
  styleUrl: './menu-drop-button.component.css'
})
export class MenuDropButtonComponent implements OnInit, AfterViewInit {
  @Input() label = 'Dropdown';
  @Input() icon?: string;
  @Input() iconPosition: 'left' | 'right' = 'right';
  @Input() type = 'button';
  @Input() disabled = false;
  @Input() customClass = '';
  @Input() menuItems?: MainMenuItem;

  showDropdown = false;
  activeIndex = 0;
  activeMenuId = '';

  @ViewChildren('mi', { read: ElementRef }) itemEls!: QueryList<ElementRef<HTMLElement>>;
  @ViewChild('triggerBtn', { read: ElementRef }) triggerBtn!: ElementRef<HTMLButtonElement>;
  @ViewChild('menuPanel',  { read: ElementRef }) menuPanel!: ElementRef<HTMLElement>;

  private _awaitingFirstFocus = false;

  constructor(
    private eRef: ElementRef,
    private router: Router,
    private routerService: RoutingService,
    private userService: UserService,
    private menuService: MenuService
  ) {}

  private static readonly MENU_ICONS: Record<string, string> = {
    globalConsumerSearch: 'fa-search',
    consumerReferral: 'fa-paper-plane',
    consumerRegistration: 'fa-user-plus',
    personalAssistantRegistration: 'fa-handshake-o',
    timesheets: 'fa-clock-o',
    pendedTimesheets: 'fa-hourglass-half',
    authorizations: 'fa-check-circle',
    invoices: 'fa-file-text-o',
    invoicePayrollProcessing: 'fa-money',
    timesheetPayrollProcessing: 'fa-calendar-check-o',
    invoiceAdjustments: 'fa-file-text',
    purchaseRequests: 'fa-shopping-cart',
    purchaseAdjustments: 'fa-cart-plus',
    independentFacilitator: 'fa-user-circle',
    processCodes: 'fa-cogs',
    serviceCodes: 'fa-tags',
    applicationConfig: 'fa-wrench',
    participantProviderRates: 'fa-dollar',
    payableProviderRates: 'fa-money',
    payrollAdjustment: 'fa-sliders',
    payrollApprovals: 'fa-thumbs-up',
    invoiceApprovals: 'fa-file-text-o',
    purchaseRequestApprovals: 'fa-shopping-cart',
    payrollResurrect: 'fa-undo',
    revertAdjustment: 'fa-exchange',
    billingClaimAdjustment: 'fa-gavel',
    bulkAdjustments: 'fa-database',
    billingAdjustment: 'fa-pencil-square-o',
    billingApprovals: 'fa-check-square-o',
    billingReports: 'fa-bar-chart',
    authUtilizationAnalysis: 'fa-line-chart',
  };

  /** Maps child/action route segments back to the parent landing_component. */
  private static readonly CHILD_TO_LANDING: Record<string, string> = (() => {
    const actionRoutes: Record<string, string[]> = {
      authorizations:              ['addAuthorizations'],
      participantLeadsList:        ['newReferral', 'referralLogic'],
      referralList:                ['newReferral', 'referralLogic'],
      participantEnrollmentList:   ['newReferral', 'referralLogic'],
      providerEnrollmentList:      ['newReferral', 'referralLogic'],
      globalConsumerSearch:        ['newReferral', 'referralLogic'],
      processCodeList:             ['processCode'],
      serviceCodeList:             ['serviceCode'],
      participantProviderRatesList: ['addParticipantProviderRates'],
      payablePersonalAssistantRates: ['addPayablePersonalAssistantRates'],
      caseManagerList:             ['caseManagerDetails'],
      bulkAdjustments:             ['addBulkAdjustment', 'bulkBatchResultList', 'bulkBatchErrorList'],
      timesheetPayrollProcessing:  ['addTimesheetPayrollBatch', 'timesheetPayrollRunResults', 'timesheetPayrollRunErrors'],
      enrollmentDetails:           ['consumerRates', 'sickTime', 'summary', 'enrollmentInformation', 'toDoList', 'service-credentials', 'authorization', 'employers', 'documents', 'forms', 'checklist', 'associations', 'signature', 'additionalAttributes', 'authorizations', 'timesheets', 'invoices', 'benefits', 'purchaseRequests'],
    };
    const map: Record<string, string> = {};
    for (const [parent, children] of Object.entries(actionRoutes)) {
      for (const child of children) {
        map[child] = parent;
      }
    }
    return map;
  })();

  getMenuIcon(menuId: string): string {
    return MenuDropButtonComponent.MENU_ICONS[menuId] || 'fa-circle-o';
  }

  /** Resolves the current URL segment to a menu_id by matching landing_component (direct or via child route). */
  private resolveActiveMenuId(): void {
    const urlSegments = this.router.url.split('/').filter(Boolean);
    // URL is like /home/<segment>/... — grab the first segment after 'home'
    const homeIdx = urlSegments.indexOf('home');
    const currentSegment = homeIdx >= 0 ? urlSegments[homeIdx + 1] : '';
    if (!currentSegment || !this.menuItems) {
      this.activeMenuId = '';
      return;
    }

    // Resolve child route to parent landing_component
    const landingComponent = MenuDropButtonComponent.CHILD_TO_LANDING[currentSegment] || currentSegment;

    // A tab page, or a child route of one (e.g. timesheetPayrollRunResults), highlights its group's
    // menu entry, which is keyed by the group's anchor menu_id
    const programCode = this.userService.getSelectedProgram()?.program_code;
    const tabGroupId = programCode
      ? this.menuService.findTabGroupId(programCode, currentSegment) ?? this.menuService.findTabGroupId(programCode, landingComponent)
      : undefined;
    if (tabGroupId) {
      this.activeMenuId = tabGroupId;
      return;
    }

    // Find the menu item whose landing_component matches
    for (const category of Object.keys(this.menuItems)) {
      for (const item of this.menuItems[category]) {
        if (item.landing_component === landingComponent) {
          this.activeMenuId = item.menu_id;
          return;
        }
      }
    }

    this.activeMenuId = '';
  }

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    this.itemEls.changes.subscribe(() => {
      if (this.activeIndex > this.itemEls.length - 1) {
        this.activeIndex = Math.max(0, this.itemEls.length - 1);
      }
      if (this._awaitingFirstFocus && this.itemEls.length > 0) {
        this._awaitingFirstFocus = false;
        this.focusActive();
      }
    });
  }

  toggleDropdown(): void {
    if (this.disabled) return;
    this.showDropdown = !this.showDropdown;
    if (this.showDropdown) {
      this.resolveActiveMenuId();
      this.activeIndex = 0;
      this.focusFirstItemWhenReady();
    }
  }

  onTriggerKeydown(event: KeyboardEvent): void {
    if (this.disabled) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!this.showDropdown) this.showDropdown = true;
      this.activeIndex = event.key === 'ArrowDown' ? 0 : Math.max(0, this.itemEls.length - 1);
      this.focusFirstItemWhenReady();
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.showDropdown = true;
      this.activeIndex = 0;
      this.focusFirstItemWhenReady();
    } else if (event.key === 'Escape' && this.showDropdown) {
      event.preventDefault();
      this.showDropdown = false;
      this.triggerBtn?.nativeElement.focus();
    }
  }

  @HostListener('document:click', ['$event'])
  clickout(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!this.eRef.nativeElement.contains(target)) {
      this.showDropdown = false;
    }
  }


  onItemKeydown(e: KeyboardEvent, item: MenuItem, idx: number): void {
    e.stopPropagation(); 

    const max = this.itemEls.length - 1;
    const go = (i: number) => { this.activeIndex = i; this.focusActive(); };

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        const next = (idx >= max) ? 0 : idx + 1; 
        go(next);
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const prev = (idx <= 0) ? max : idx - 1; 
        go(prev);
        break;
      }
      case 'ArrowLeft': {
        e.preventDefault();
        break;
      }
      case 'ArrowRight': {
        e.preventDefault();
        break;
      }
      case 'Home':
        e.preventDefault(); go(0); break;
      case 'End':
        e.preventDefault(); go(max); break;

      case 'Tab': {
        e.preventDefault();
        if (e.shiftKey) {
          this.closeAndFocusPrev();
        } else {
          this.closeAndFocusNext();
        }
        break;
      }

      case 'Escape':
        e.preventDefault();
        this.showDropdown = false;
        this.triggerBtn?.nativeElement.focus();
        break;

      case 'Enter':
      case ' ':
      case 'Spacebar':
        e.preventDefault();
        e.stopPropagation();
        this.OnMenuClick(item);
        break;
    }
  }

  private closeAndFocusNext(): void {
    this.showDropdown = false;
    this.focusNextAfter(this.menuPanel?.nativeElement || this.triggerBtn.nativeElement);
  }

  private closeAndFocusPrev(): void {
    this.showDropdown = false;
    this.focusPrevBefore(this.menuPanel?.nativeElement || this.triggerBtn.nativeElement);
  }

  OnMenuClick(item: MenuItem): void {
    this.showDropdown = false;
    const route = `/home/${item.landing_component}`;
    this.userService.setFirstSegment(item.landing_component ?? '');
    setTimeout(() => this.routerService.navigate(route), 0);
  }

  objectKeys(obj: any): string[] {
    return Object.keys(obj);
  }

  getFlatIndex(category: string, innerIndex: number): number {
    if (!this.menuItems) return -1;
    let idx = 0;
    for (const cat of this.objectKeys(this.menuItems)) {
      const list = this.menuItems[cat] ?? [];
      for (let i = 0; i < list.length; i++) {
        const it = list[i];
        if (it.display === 'y') {
          if (cat === category && i === innerIndex) return idx;
          idx++;
        }
      }
    }
    return -1;
  }

  private focusFirstItemWhenReady(): void {
    if (this.itemEls && this.itemEls.length > 0) {
      this.focusActive();
      return;
    }
    this._awaitingFirstFocus = true;
    setTimeout(() => {
      if (this.itemEls && this.itemEls.length > 0) {
        this._awaitingFirstFocus = false;
        this.focusActive();
      }
    }, 0);
  }

  private focusActive(): void {
    const el = this.itemEls.get(this.activeIndex)?.nativeElement;
    el?.focus();
  }

  private focusNextAfter(rootEl: HTMLElement): void {
    const all = this.getFocusable();
    const current = document.activeElement as HTMLElement | null;
    const startIdx = current ? all.indexOf(current) : all.indexOf(rootEl);
    const next = all[startIdx + 1] || all[0];
    next?.focus();
  }

  private focusPrevBefore(rootEl: HTMLElement): void {
    const all = this.getFocusable();
    const current = document.activeElement as HTMLElement | null;
    const startIdx = current ? all.indexOf(current) : all.indexOf(rootEl);
    const prev = all[startIdx - 1] || all[all.length - 1];
    prev?.focus();
  }

  private getFocusable(): HTMLElement[] {
    const sel = [
      'a[href]',
      'area[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');
    const nodes = Array.from(document.querySelectorAll<HTMLElement>(sel));
    return nodes.filter(el => this.isVisible(el));
  }

  private isVisible(el: HTMLElement): boolean {
    const style = window.getComputedStyle(el);
    return style.visibility !== 'hidden' && style.display !== 'none';
  }
}
